import { CreativeTypes } from '@bynder-studio/render-core';
import { Creative } from 'packages/store/creatives/types';
import { type ExportResult } from './types';
import { type RenditionConfiguration } from 'packages/services/renditionsService';

export const getUniqueCreativeTypesFromDesignList = (designs: Creative[]) => {
    const uniqueCreativeTypes = new Set<CreativeTypes>();

    designs.forEach((design) => {
        if (design.creativeType) {
            uniqueCreativeTypes.add(design.creativeType);
        }
    });

    return Array.from(uniqueCreativeTypes);
};

export const getExportableDesignsFromDesignList = (designs: Creative[]) => {
    return designs.filter((design) => design.videosCount > 0);
};

export const isDesignCompatibleWithProfiles = (
    design: Creative,
    selectedProfiles: Record<CreativeTypes, RenditionConfiguration> | undefined,
): boolean => {
    if (!selectedProfiles) {
        return true;
    }

    const profile = selectedProfiles[design.creativeType];

    if (!profile) {
        return true;
    }

    if (!design.exportConfigurationIds || design.exportConfigurationIds.length === 0) {
        return true;
    }

    return design.exportConfigurationIds.includes(profile.id);
};

export const getProfileCompatibleDesigns = (
    designs: Creative[],
    selectedProfiles: Record<CreativeTypes, RenditionConfiguration> | undefined,
): Creative[] => {
    return designs.filter((design) => isDesignCompatibleWithProfiles(design, selectedProfiles));
};

export const getIncompatibleDesignsCount = (
    designs: Creative[],
    selectedProfiles: Record<CreativeTypes, RenditionConfiguration> | undefined,
): number => {
    return designs.filter((design) => !isDesignCompatibleWithProfiles(design, selectedProfiles)).length;
};

export const getPreferredConfigurationIds = (
    designs: Creative[],
    creativeType: CreativeTypes,
): number[] | undefined => {
    const restrictedDesigns = designs.filter(
        (d) => d.creativeType === creativeType && d.exportConfigurationIds && d.exportConfigurationIds.length > 0,
    );

    if (restrictedDesigns.length === 0) {
        return undefined;
    }

    const union = new Set<number>();
    restrictedDesigns.forEach((design) => {
        design.exportConfigurationIds!.forEach((id) => union.add(id));
    });

    return union.size > 0 ? Array.from(union) : undefined;
};

export const collectAlertMessagesFromDesignList = (designs: Creative[], exportResults: ExportResult[]) => {
    const messages: string[] = [];
    const designsMap = new Map<number, Creative>();
    designs.forEach((design) => {
        designsMap.set(design.id, design);
    });

    // fist collect all designs without variations
    const designsWithoutVariations = designs.filter((design) => design.videosCount === 0);

    if (designsWithoutVariations.length === 1) {
        messages.push(
            `The design "${designsWithoutVariations[0].name}" does not have any variations. It will not be exported.`,
        );
    }

    if (designsWithoutVariations.length > 1) {
        messages.push(
            `The following designs do not have any variations: ${designsWithoutVariations
                .map((design) => `"${design.name}"`)
                .join(', ')}. They will not be exported.`,
        );
    }

    // exports errors
    exportResults.forEach((exportResult) => {
        if (exportResult.success === true) {
            return;
        }

        const design = designsMap.get(exportResult.designId);

        if (!design) {
            return;
        }

        messages.push(
            `Export of design "${design.name}" failed with error: "${exportResult.message || 'No Message'}".`,
        );
    });

    return messages.join('\n');
};
